import {
  type PlanOrganizationTask,
  PlanOrganizationTaskName,
  PlanOrganizationTaskStatus
} from '../models/PlanOrganizationTask'
import { useEffect, useState } from 'react'
import {
  CanModifyPlanOrganization,
  GetAnnualPlanStatus,
  GetRevisionRequests,
  UpdatePlanOrganizationTaskStatus
} from '../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { type PriorityActivity } from '../models/PriorityActivity'
import { extractTaggedUserEmails } from './use-comments'
import { CommentParentType, type NewCommentValues } from '../models/Comment'
import { AddComment } from '../services/CommentService'
import { AnnualPlanRole } from '../models/AnnualPlanRole'
import { AnnualPlanStatus, PassedContractPhase } from '../models/AnnualPlanStatus'
import { type PlanRevisionRequest } from '../models/PlanRevisionRequest'
import { handleApiResponse } from '../utils/apiHelpers'

interface UseUpdatePlanOrgTaskStatusType {
  isUpdating: boolean
  statusForLockModal: PlanOrganizationTaskStatus | null
  statusForSomeItemsLockModal: PlanOrganizationTaskStatus | null
  statusForSubmittedToInProgressModal: PlanOrganizationTaskStatus | null
  statusForSubmittedToAcceptedModal: PlanOrganizationTaskStatus | null
  statusForSubmittedToNeedsRevisionModal: PlanOrganizationTaskStatus | null
  setStatusForLockModal: React.Dispatch<React.SetStateAction<PlanOrganizationTaskStatus | null>>
  setStatusForSomeItemsLockModal: React.Dispatch<React.SetStateAction<PlanOrganizationTaskStatus | null>>
  setStatusForSubmittedToInProgressModal: React.Dispatch<React.SetStateAction<PlanOrganizationTaskStatus | null>>
  setStatusForSubmittedToAcceptedModal: React.Dispatch<React.SetStateAction<PlanOrganizationTaskStatus | null>>
  setStatusForSubmittedToNeedsRevisionModal: React.Dispatch<React.SetStateAction<PlanOrganizationTaskStatus | null>>
  handleConfirmLockModal: () => void
  updatePlanOrgTaskStatus: (status: PlanOrganizationTaskStatus) => void
  handleConfirmSubmittedToNeedsRevision: (comment: string, alreadyLeftFeedback: boolean) => Promise<void>
  disableStatus: boolean
  readOnly: boolean
  trackingReadonly: boolean
}

interface Props {
  userRole: AnnualPlanRole | null
  annualPlanId: number
  planOrganizationTask?: PlanOrganizationTask
  onPlanOrgTaskUpdate?: (updatedTask: PlanOrganizationTask) => void
  priorityActivities?: PriorityActivity[]
  onBack?: () => void
  contractPhase: boolean
}

export const useUpdatePlanOrgTaskStatus = (props: Props): UseUpdatePlanOrgTaskStatusType => {
  const [statusForLockModal, setStatusForLockModal] = useState<PlanOrganizationTaskStatus | null>(null)
  const [statusForSomeItemsLockModal, setStatusForSomeItemsLockModal] = useState<PlanOrganizationTaskStatus | null>(null)
  const [statusForSubmittedToInProgressModal, setStatusForSubmittedToInProgressModal] = useState<PlanOrganizationTaskStatus | null>(null)
  const [statusForSubmittedToAcceptedModal, setStatusForSubmittedToAcceptedModal] = useState<PlanOrganizationTaskStatus | null>(null)
  const [statusForSubmittedToNeedsRevisionModal, setStatusForSubmittedToNeedsRevisionModal] = useState<PlanOrganizationTaskStatus | null>(null)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [revisionRequests, setRevisionRequests] = useState<PlanRevisionRequest[]>([])
  const [canModifyPlanOrganization, setCanModifyPlanOrganization] = useState(false)
  const [annualPlanStatus, setAnnualPlanStatus] = useState<AnnualPlanStatus>(AnnualPlanStatus.NotStarted)

  const task = props.planOrganizationTask
  const isMeetingSchedule = task?.taskName === PlanOrganizationTaskName.RcnOperations

  // A task needs revision if the plan needs revision and the task has an unresolved revision request,
  // or if all priorities need revision, unless the task is Meeting Schedule, which is not considered
  // to be part of all priorities.
  const needsRevision = (annualPlanStatus === AnnualPlanStatus.NeedsRevision || annualPlanStatus === AnnualPlanStatus.UpdatesNeedsRevision) &&
    revisionRequests.some((request) =>
      !request.resolved &&
      (request.taskName === task?.taskName ||
        (request.taskName === PlanOrganizationTaskName.AllPriorities && task?.taskName !== PlanOrganizationTaskName.RcnOperations)
      )
    )

  // Implementation status is read-only if the task status is Updates Ready, Updates Submitted,
  // or Tracking Completed, and the task does not need revision.
  const isImplementationStatusReadOnly = (task?.status === PlanOrganizationTaskStatus.UpdatesReady ||
    task?.status === PlanOrganizationTaskStatus.UpdatesSubmitted ||
    task?.status === PlanOrganizationTaskStatus.TrackingCompleted) && !needsRevision

  // A task is readonly if it is meeting schedules and the user is not a Plan Administrator
  // or Plan Reviewer, and for other tasks the status is Accepted or Submitted while does
  // not need revision.
  const readOnly = (!canModifyPlanOrganization && !needsRevision) ||
    (isMeetingSchedule && ![AnnualPlanRole.PlanAdministrator, AnnualPlanRole.PlanReviewer].includes(props.userRole as AnnualPlanRole)) ||
    ((props.userRole as AnnualPlanRole) === AnnualPlanRole.PlanReviewerReadOnly) ||
    ((task?.status === PlanOrganizationTaskStatus.Accepted ||
      task?.status === PlanOrganizationTaskStatus.Submitted) && !needsRevision) ||
    isImplementationStatusReadOnly || (PassedContractPhase.includes(annualPlanStatus) && props.contractPhase)

  const canUpdateTracking = task?.status === PlanOrganizationTaskStatus.UpdatesSubmitted
  const trackingReadonly = readOnly && !canUpdateTracking

  // The status is disabled if the task is meeting schedules and the user is not a
  // Plan Administrator or Plan Reviewer, and for other tasks the status is disabled if user is a
  // Plan Contributor and the task status is Accepted.
  const disableStatus = !canModifyPlanOrganization ||
    (isMeetingSchedule && ![AnnualPlanRole.PlanAdministrator, AnnualPlanRole.PlanReviewer].includes(props.userRole as AnnualPlanRole)) ||
    (props.userRole === AnnualPlanRole.PlanContributor && task?.status === PlanOrganizationTaskStatus.Accepted)

  const loadPlanStatus = async (): Promise<void> => {
    const status = await handleApiResponse(GetAnnualPlanStatus(props.annualPlanId))
    if (status != null) setAnnualPlanStatus(status)
  }

  const loadRevisionRequests = async (): Promise<void> => {
    const requests = await handleApiResponse(GetRevisionRequests(props.annualPlanId))
    if (requests != null) setRevisionRequests(requests)
  }

  const loadCanModifyPlanOrganization = async (): Promise<void> => {
    if (task == null) {
      return
    }

    try {
      const canModify = await handleApiResponse(CanModifyPlanOrganization(task.planOrganizationId, props.contractPhase))
      if (canModify != null) setCanModifyPlanOrganization(canModify)
    } catch (error) {
      toast.error('Unexpected error occurred while checking if user can modify plan organization')
      console.error(error)
    }
  }

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      await Promise.all([
        loadPlanStatus(),
        loadRevisionRequests(),
        loadCanModifyPlanOrganization()
      ])
    }

    if (task != null) {
      void loadData()
    }
  }, [props.annualPlanId, task?.planOrganizationId])

  const updatePlanOrgTaskStatus = (newStatus: PlanOrganizationTaskStatus): void => {
    if (task == null) {
      return
    }

    const asyncUpdatePlanOrgTaskStatus = async (newStatus: PlanOrganizationTaskStatus): Promise<void> => {
      setIsUpdating(true)

      const { success, errors } = await UpdatePlanOrganizationTaskStatus(task.id, newStatus)

      if (!success) {
        if (errors.length > 0) errors.forEach((e) => toast.error(e))
        else toast.error('An error occurred while updating the status.')
      } else {
        const updatedTask = { ...task, status: newStatus }
        props.onPlanOrgTaskUpdate?.(updatedTask)

        if (task.status === PlanOrganizationTaskStatus.Submitted && newStatus === PlanOrganizationTaskStatus.Accepted) {
          toast.success('This task has been accepted')
        }
      }

      setIsUpdating(false)
    }

    // Plan Contributor cannot update status when the task is accepted
    if (props.userRole === AnnualPlanRole.PlanContributor && task.status === PlanOrganizationTaskStatus.Accepted) {
      return
    }

    if (task.status === PlanOrganizationTaskStatus.NotStarted && newStatus === PlanOrganizationTaskStatus.Submitted) {
      toast.error('You cannot submit a task that has not been started.')
      return
    }

    if (task.status !== PlanOrganizationTaskStatus.Submitted && newStatus === PlanOrganizationTaskStatus.Accepted) {
      toast.error('You cannot accept a task that has not been submitted.')
      return
    }

    // Submitted => Accepted (9)
    if (statusForSubmittedToAcceptedModal == null && task.status === PlanOrganizationTaskStatus.Submitted && newStatus === PlanOrganizationTaskStatus.Accepted) {
      setStatusForSubmittedToAcceptedModal(newStatus)
      return
    }

    // Submitted => Needs Revision (8)
    if (statusForSubmittedToNeedsRevisionModal == null && task.status === PlanOrganizationTaskStatus.Submitted && newStatus === PlanOrganizationTaskStatus.NeedsRevision) {
      setStatusForSubmittedToNeedsRevisionModal(newStatus)
      return
    }

    // Submitted => In Progress (7)
    if (statusForSubmittedToInProgressModal == null && task.status === PlanOrganizationTaskStatus.Submitted && newStatus === PlanOrganizationTaskStatus.InProgress) {
      setStatusForSubmittedToInProgressModal(newStatus)
      return
    }

    // Needs Revision or In Progress => Submitted and some activities have data (6)
    const someActivitiesNotStarted = props.priorityActivities?.some(activity => !activity.started) ?? false
    if (statusForSomeItemsLockModal == null && [PlanOrganizationTaskStatus.NeedsRevision, PlanOrganizationTaskStatus.InProgress].includes(task.status) && newStatus === PlanOrganizationTaskStatus.Submitted && someActivitiesNotStarted) {
      setStatusForSomeItemsLockModal(newStatus)
      return
    }

    // Needs Revision or In Progress => Submitted and all activities have data (6)
    const allActivitiesStarted = props.priorityActivities?.every(activity => activity.started) ?? false
    if (statusForLockModal == null && [PlanOrganizationTaskStatus.NeedsRevision, PlanOrganizationTaskStatus.InProgress].includes(task.status) && newStatus === PlanOrganizationTaskStatus.Submitted && allActivitiesStarted) {
      setStatusForLockModal(newStatus)
      return
    }

    void asyncUpdatePlanOrgTaskStatus(newStatus)
  }

  const handleConfirmLockModal = (): void => {
    if (statusForLockModal != null) {
      updatePlanOrgTaskStatus(statusForLockModal)
      setStatusForLockModal(null)
    }
    if (statusForSomeItemsLockModal != null) {
      updatePlanOrgTaskStatus(statusForSomeItemsLockModal)
      setStatusForSomeItemsLockModal(null)
    }
    if (statusForSubmittedToInProgressModal != null) {
      updatePlanOrgTaskStatus(statusForSubmittedToInProgressModal)
      setStatusForSubmittedToInProgressModal(null)
    }
    if (statusForSubmittedToAcceptedModal != null) {
      updatePlanOrgTaskStatus(statusForSubmittedToAcceptedModal)
      setStatusForSubmittedToAcceptedModal(null)
    }
  }

  const handleConfirmSubmittedToNeedsRevision = async (comment: string, alreadyLeftFeedback: boolean): Promise<void> => {
    if (statusForSubmittedToNeedsRevisionModal == null) return

    if (comment === '' && !alreadyLeftFeedback) {
      toast.error('If you do not leave a comment here, you must select the “I have already provided feedback” option to continue.')
      return
    }

    if (comment !== '' && !alreadyLeftFeedback) {
      updatePlanOrgTaskStatus(statusForSubmittedToNeedsRevisionModal)

      const taggedUserEmails = extractTaggedUserEmails(comment)

      const newCommentValues: NewCommentValues = {
        commentText: comment,
        taggedUserEmails,
        parentType: CommentParentType.PlanOrganizationTask,
        parentId: task?.id ?? 0
      }

      const { response, success, errors } = await AddComment(newCommentValues)

      if (!success || response?.id === null) {
        errors.forEach((e) => toast.error(e))
        return
      }
    }

    if (comment === '' && alreadyLeftFeedback) {
      updatePlanOrgTaskStatus(statusForSubmittedToNeedsRevisionModal)
    }

    if (comment !== '' && alreadyLeftFeedback) {
      updatePlanOrgTaskStatus(statusForSubmittedToNeedsRevisionModal)
    }

    setStatusForSubmittedToNeedsRevisionModal(null)
  }

  return {
    isUpdating,
    handleConfirmLockModal,
    updatePlanOrgTaskStatus,
    statusForLockModal,
    setStatusForLockModal,
    statusForSomeItemsLockModal,
    setStatusForSomeItemsLockModal,
    statusForSubmittedToInProgressModal,
    setStatusForSubmittedToInProgressModal,
    statusForSubmittedToAcceptedModal,
    setStatusForSubmittedToAcceptedModal,
    statusForSubmittedToNeedsRevisionModal,
    setStatusForSubmittedToNeedsRevisionModal,
    handleConfirmSubmittedToNeedsRevision,
    disableStatus,
    readOnly,
    trackingReadonly
  }
}
